import React, {createContext, FC, useEffect, useState, Dispatch, SetStateAction, ReactNode} from 'react'
import {ID} from '@/_metronic/helpers'
import {Customer, CustomerIndicators, initialIndicators} from '../core/_models'
import {getCustomer, getCustomerIndicators} from '../core/_requests'
import {Settings, initialSettings} from '../../settings/core/_models'
import {getSettings} from '../../settings/core/_requests'

type CustomerContextType = {
  customer?: Customer
  setCustomer: Dispatch<SetStateAction<Customer|undefined>>
  indicators: CustomerIndicators
  settings: Settings
  refresh: () => Promise<void>
}

const CustomerContext = createContext<CustomerContextType>({
  customer: undefined,
  indicators: initialIndicators,
  setCustomer: () => {},
  settings: initialSettings,
  refresh: async () => {},
})

type Props = {
  customerId: ID
  children: ReactNode
}

const CustomerProvider: FC<Props> = ({children, customerId}) => {
  const [customer, setCustomer] = useState<Customer|undefined>()
  const [settings, setSettings] = useState<Settings>(initialSettings)
  const [indicators, setIndicators] = useState<CustomerIndicators>(initialIndicators)
  const [isLoading, setIsLoading] = useState<boolean>(true)

  const init = async () => {
    try {
      setIsLoading(true)
      setCustomer(await getCustomer(customerId))
      const _ = await getSettings()
      void(_ !== undefined && setSettings(_))
      getCustomerIndicators(customerId).then(res => {
        void(res && setIndicators(res))
      })
    } catch (e: unknown) {
      console.log('Customer init failed', e)
    } finally {
      setIsLoading(false)
    }
  }

  useEffect(() => {
    void(customerId && isLoading && init())
  }, [isLoading, customerId])

  if (isLoading) {
    return (<>Loading ...</>)
  }

  if (!isLoading && customer === undefined) {
    return (<>No customer found</>)
  }

  return (
    <CustomerContext.Provider value={{
      settings,
      customer,
      setCustomer,
      indicators,
      refresh: init, // Ajout de refresh au Provider
    }}>
      {children}
    </CustomerContext.Provider>
  )
}

export {CustomerContext, CustomerProvider}
